import {
  animate,
  ViewOpts,
  ImageView,
  View,
  ImageSliceView,
} from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import i18n from 'src/lib/i18n/i18n';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import sounds from 'src/lib/sounds';
import buttons from 'src/lib/ui/config/buttons';

interface Match2TipOpts extends ViewOpts {
  onOk?: () => void;
}

export class Match2Tip extends View {
  private panel: ImageSliceView;
  private pointer: ImageView;
  private message: LangBitmapFontTextView;
  private okButton: ButtonScaleViewWithText;
  private position = { x: 0, y: 0 };
  public onOk?: () => void;
  private time = 0;
  private baseHeight = 0;

  constructor(opts: Match2TipOpts = {}) {
    super({
      visible: false,
      ...opts,
    });

    this.onOk = opts.onOk;

    this.panel = new ImageSliceView({
      superview: this,
      image: 'assets/minigame/match2/frame.tooltip.png',
      width: 553,
      height: 300,
      centerOnOrigin: true,
      slices: { top: 31, bot: 33 },
    });

    this.message = new LangBitmapFontTextView({
      superview: this.panel,
      font: 'Body',
      color: '#534020',
      size: 26,
      localeText: () => 'TITLE',
      width: 440,
      height: 200,
      align: 'center',
      x: 50,
      y: 45,
      wordWrap: true,
    });

    this.okButton = new ButtonScaleViewWithText({
      ...buttons.secondary,
      superview: this.panel,
      width: 200,
      height: 80,
      fontSize: 40,
      labelOffsetY: -2,
      font: 'Body',
      localeText: () => i18n('basic.okay'),
      onClick: async () => {
        sounds.sfx.play('ok');
        this.onOk?.();
      },
      tracking: {
        feature: 'match2-block-tip',
        $subFeature: 'ok',
      },
      x: this.panel.style.width * 0.5 - 100,
      y: this.panel.style.height - 130,
    });

    this.pointer = new ImageView({
      superview: this,
      image: 'assets/tutorial/hand/tutorial_hand.png',
      width: 96,
      height: 80,
      centerOnOrigin: true,
      scaleX: 0,
      scaleY: 0,
    });
  }

  public showTip(id: string, position: { x: number; y: number }) {
    this.baseHeight = this.style.height;
    this.position = position;
    const group = 'tips';
    this.message.updateOpts({
      localeText: () => i18n(`minigame.match2.${group}.${id}`),
    });
    const upper = this.style.height * 0.2;
    const lower = this.style.height * 0.8;
    const mid = this.style.height * 0.5;
    this.panel.updateOpts({
      x: this.style.width / 2,
      y: position.y > mid ? upper : lower,
    });
    this.pointer.updateOpts({
      x: position.x + 80,
      y: position.y + 100,
    });
    this.animateShow();
  }

  public async hideTip() {
    await this.animateHide();
  }

  public tick(dt: number) {
    this.time += dt;
    this.pointer.updateOpts({
      offsetY: Math.abs(Math.sin(this.time * 0.005) * 30),
    });
  }

  private async animateShow() {
    if (this.style.visible) return;
    this.updateOpts({ visible: true });

    await animate(this.panel)
      .now({ scaleY: 0 })
      .then({ scaleY: 1 }, 400, animate.easeOutBack)
      .toPromise();

    await animate(this.pointer)
      .now({ scaleX: 0, scaleY: 0 })
      .then({ scaleX: 1, scaleY: 1 }, 400, animate.easeOutBack)
      .toPromise();
  }

  private async animateHide() {
    if (!this.style.visible) return;

    animate(this.panel)
      .now({ scaleY: 1 })
      .then({ scaleY: 0 }, 300, animate.easeInBack)
      .toPromise();

    await animate(this.pointer)
      .now({ scaleX: 1, scaleY: 1 })
      .then({ scaleX: 0, scaleY: 0 }, 300, animate.easeInBack)
      .toPromise();

    this.updateOpts({ visible: false });
  }

  public resize(size: { height: number }) {
    if (!this.style.visible) return;
    this.updateOpts({ height: size.height });
    const heightDiff = size.height - this.baseHeight;
    const upper = this.style.height * 0.2;
    const lower = this.style.height * 0.8;
    const mid = this.style.height * 0.5;
    this.panel.updateOpts({
      y: this.position.y > mid ? upper : lower,
    });
    this.pointer.updateOpts({
      y: this.position.y + 100 + heightDiff / 2,
    });
  }
}
